
import { cn } from "@/lib/utils";
import React from 'react';

interface ValuePropositionProps {
  title: string;
  description: string;
  image?: string;
  imageAlt?: string;
  reverse?: boolean;
  backgroundColor?: string;
  children?: React.ReactNode;
  id?: string;
  imageOverlay?: boolean;
  overlayColor?: string;
  overlayOpacity?: string;
  highlightWords?: boolean;
  icon?: React.ReactNode;
  smallerPadding?: boolean;
  textAlignment?: string;
  containerWidth?: string;
  noDivider?: boolean;
}

const ValueProposition = ({
  title,
  description,
  image,
  imageAlt = "Illustration",
  reverse = false,
  backgroundColor = "bg-white",
  children,
  id,
  imageOverlay = false,
  overlayColor = "bg-black",
  overlayOpacity = "opacity-30",
  highlightWords = false,
  icon,
  smallerPadding = false,
  textAlignment = "text-center",
  containerWidth = "max-w-4xl",
  noDivider = false
}: ValuePropositionProps) => {
  const enhancedAltText = imageAlt === "Illustration" ? `Jörg Hörmann - ${title}` : imageAlt;
  
  const processDescription = (text: string) => {
    if (!highlightWords) return text;
    
    const keyWords = ['Führung', 'Strategie', 'Emotion', 'Intelligenz', 'Katalysator', 'Innovation', 'Entwicklung', 'Balance'];
    let processedText = text;
    
    keyWords.forEach(word => {
      const regex = new RegExp(`\\b${word}\\b`, 'gi');
      processedText = processedText.replace(regex, `<span class="text-joerg-800 font-medium">$&</span>`);
    });
    
    return processedText;
  };
  
  return (
    <section 
      className={cn(
        smallerPadding ? "section-spacing" : "section-spacing", 
        "relative",
        backgroundColor
      )} 
      id={id}
    >
      <div className="absolute inset-0 bg-pattern opacity-5 pointer-events-none"></div>
      
      <div className={cn("container relative mx-auto", containerWidth)}>
        <div className={cn("flex flex-col", textAlignment)}>
          {icon && (
            <div className="mb-8 text-center transform hover:scale-110 transition-transform duration-300">
              {icon}
            </div>
          )}
          
          <h2 className={cn(
            "text-3xl md:text-4xl font-bold mb-6 leading-tight text-joerg-800",
            textAlignment === "text-center" ? "text-center" : "text-left"
          )}>
            {title}
          </h2>
          
          {!noDivider && (
            <div className={cn(
              "w-20 h-1 bg-primary mb-8 transform hover:scale-x-110 transition-transform duration-300",
              textAlignment === "text-center" ? "mx-auto" : "ml-0"
            )}></div>
          )}
          
          {highlightWords ? (
            <div 
              className={cn(
                "text-joerg-700 text-lg leading-relaxed mb-6", 
                textAlignment === "text-left" ? "mx-0 max-w-3xl" : "max-w-prose mx-auto"
              )}
              dangerouslySetInnerHTML={{
                __html: processDescription(description)
              }} 
            /> 
          ) : (
            <p className={cn(
              "text-joerg-700 text-lg leading-relaxed mb-6", 
              textAlignment === "text-left" ? "mx-0 max-w-3xl" : "max-w-prose mx-auto"
            )}>
              {description}
            </p>
          )}
          
          {image && (
            <div className="mt-8 w-full">
              <div className="relative rounded-2xl shadow-md overflow-hidden">
                <div className="bg-gray-100 animate-pulse absolute inset-0"></div>
                <img 
                  src={image} 
                  alt={enhancedAltText} 
                  className="w-full h-auto object-cover rounded-2xl relative z-10 force-visible" 
                  loading="eager"
                  fetchPriority="high"
                  decoding="sync"
                  onError={(e) => {
                    console.error(`Failed to load image: ${(e.target as HTMLImageElement).src}`);
                    const target = e.target as HTMLImageElement;
                    target.style.visibility = 'hidden';
                    
                    const parent = target.parentElement;
                    if (parent) {
                      const fallback = document.createElement('div');
                      fallback.className = "w-full aspect-video bg-gray-100 flex items-center justify-center flex-col p-4";
                      
                      const icon = document.createElement('div');
                      icon.className = "text-gray-400 mb-2";
                      icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>';
                      
                      const text = document.createElement('p');
                      text.className = "text-sm text-gray-500";
                      text.textContent = `Bild konnte nicht geladen werden: ${enhancedAltText}`;
                      
                      fallback.appendChild(icon);
                      fallback.appendChild(text);
                      parent.appendChild(fallback);
                    }
                  }}
                  onLoad={(e) => {
                    // Remove loading state when image loads
                    const target = e.currentTarget;
                    const parent = target.parentElement;
                    if (parent) {
                      const placeholder = parent.querySelector('.animate-pulse');
                      if (placeholder) {
                        placeholder.classList.add('hidden');
                      }
                    }
                  }}
                />
                {imageOverlay && (
                  <div 
                    className={cn(
                      "absolute inset-0 rounded-2xl", 
                      overlayColor, 
                      overlayOpacity
                    )}
                  ></div>
                )}
              </div>
            </div>
          )}
          
          {children && (
            <div className={cn(
              "mt-8", 
              textAlignment === "text-left" ? "mx-0 max-w-3xl" : "mx-auto max-w-prose"
            )}>
              {children}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ValueProposition;
